import {
  Grid,
  Icon,
  TagLabel,
  DarkBackgroundButton,
  Colors,
  Heading,
  Typography,
} from "@zapier/design-system";
import { css } from "@emotion/react";
import { daysFromNow } from "@zapier/date";
import { isPast } from "date-fns";

import { emitEvent, getServerUrl } from "@zapier/trials/src/utils";
import { InteractionName } from "@zapier/trials/src/utils/emitEvent/trackingTypes";

const Styles = {
  root: css({
    backgroundColor: Colors.earth,
    color: Colors.neutral100,
    padding: "45px 40px 30px",
  }),

  title: css({
    ...Typography.subHeader3Bold,
  }),

  description: css({
    marginTop: "10px",
    ...Typography.paragraph3,
  }),

  heading: css({
    display: "flex",
    alignItems: "flex-start",
    gap: "20px",
  }),
  cta: css({
    marginTop: "30px",
  }),
};

type Props = {
  title: string;
  description?: string;
  trialEndDate?: string | null;
};

const formatTrialEndDate = (trialEndDate?: string | null) => {
  if (!trialEndDate || isPast(trialEndDate)) return;

  const trialDaysRemaining = daysFromNow(trialEndDate);
  const daysText = trialDaysRemaining === 1 ? "day" : "days";

  return `${trialDaysRemaining} ${daysText} left`;
};

const onUpgradeCTAClick = () => {
  emitEvent(InteractionName.trialHubUpgradeCTAClicked, {
    event_action: "click",
    option_selected: "",
  });
};

export const TrialHubHeader = ({ title, description, trialEndDate }: Props) => {
  const formattedTrialEndDate = formatTrialEndDate(trialEndDate);

  return (
    <header css={Styles.root}>
      {/* There is a nested span here due to a `all: unset` bug in Zinnia. */}
      <div css={Styles.heading}>
        <Heading>
          <span css={Styles.title}>{title}</span>
        </Heading>
        {formattedTrialEndDate && (
          <TagLabel color="peach">
            <Grid alignItems="center" columns="auto max-content" gap={5}>
              <Icon name="miscTimelapse" size={18} />
              <span>{formattedTrialEndDate}</span>
            </Grid>
          </TagLabel>
        )}
      </div>
      {description && <p css={Styles.description}>{description}</p>}

      <div css={Styles.cta}>
        <DarkBackgroundButton
          color="primary"
          href={`${getServerUrl()}/app/pricing`}
          onClick={() => onUpgradeCTAClick()}
        >
          Upgrade anytime
        </DarkBackgroundButton>
      </div>
    </header>
  );
};
